import { Col, DatePicker, InputNumber, notification, Row } from 'antd';
import {
  DeleteOutlined,
  DollarCircleOutlined,
  DownloadOutlined,
  PlusSquareFilled,
} from '@ant-design/icons';
import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import {
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import IconButton from '@mui/material/IconButton';
import moment from 'moment-timezone';
import IMask from 'imask';
import dayjs from 'dayjs';
import clsx from 'clsx';

import { creditHttp, InterestCalculateResponse } from '@network';
import { dateFormatter, moneyFormatter, rbac } from '@utils';
import { ConfirmDelete } from '@components';
import { useQuery, useRequest } from '@hooks';
import { Credit } from '@types';
import { InterestRow as IInterestRow } from '@network/credit-http';
import { useInterestReport } from '@hooks/use-interest-report';
import cls from './CreditInterestWidget.module.css';


interface Props {
  credit: Credit;
  creditId: number;
  isModalVisible: boolean;
  refreshCredit?: () => Promise<void>;
  group?: null | string;
  isPreview?: boolean;
}

interface CalcHeaderProps {
  calc?: InterestCalculateResponse;
  error?: string;
}

interface CalculateFutureBalanceParams {
  finalBalance: number;
  capitalBalance: number;
  interestRateValue: number;
  fromMoment: moment.Moment;
  toMoment: moment.Moment;
}

interface CalculateMixedFutureBalanceParams {
  finalBalance: number;
  capitalBalance: number;
  interestRateValue: number;
  moratoryRateValue: number;
  ptMoment: moment.Moment;
  paymentMoment: moment.Moment;
  futureMoment: moment.Moment;
}

interface CalculateMoratoryFutureBalanceParams {
  finalBalance: number;
  capitalBalance: number;
  moratoryRateValue: number;
  fromMoment: moment.Moment;
  futureMoment: moment.Moment;
}

interface CalculateInterestAmountParams {
  capitalBalance: number;
  rate: number;
  days: number;
  dateMoment: moment.Moment;
}

const tz = 'Etc/UTC';

const getDaysInYear = (dateMoment: moment.Moment) => (dateMoment.isLeapYear() ? 366 : 365);

const calculateInterestAmount = ({
  capitalBalance,
  rate,
  days,
  dateMoment,
}: CalculateInterestAmountParams) => (
  (capitalBalance * (rate / 100) / getDaysInYear(dateMoment)) * days
);

const CalcHeader = ({ calc, error }: CalcHeaderProps) => (
  <div className={cls.box}>
    <Row>
      <Col xs={4} className={cls.col}>
        <div className={cls.title}>Credit</div>
        <div>{money(calc?.credit || 0)}{calc?.isUsd && ' USD'}</div>
      </Col>
      <Col xs={4} className={cls.col}>
        <div className={cls.title}>Interest Rate</div>
        <div>{calc?.interestRate || 0} %</div>
      </Col>
      <Col xs={4} className={cls.col}>
        <div className={cls.title}>Moratory Rate</div>
        <div>{calc?.moratoryRate || 0} %</div>
      </Col>
      <Col xs={4} className={cls.col}>
        <div className={cls.title}>Disbursement date</div>
        <div>{calc?.signatureDate && dateFormatter.toDateSlash(calc.signatureDate)}</div>
      </Col>
      <Col xs={4} className={cls.col}>
        <div className={cls.title}>Payment Date</div>
        <div>{calc?.paymentDate && dateFormatter.toDateSlash(calc.paymentDate)}</div>
      </Col>
      <Col xs={4} className={cls.col}>
        <div className={cls.title}>Final Payment</div>
        <div>{money(calc?.finalPayment || 0)}{calc?.isUsd && ' USD'}</div>
      </Col>
    </Row>
    {error && <div className={cls.error}>{error}</div>}
  </div>
);

export const CreditInterestWidget = ({
  credit, creditId, group, isModalVisible, refreshCredit, isPreview,
}: Props) => {
  const { search } = useLocation();
  const query = useQuery();
  const todayDate = query.get('today') ?? undefined;
  const [calc, setCalc] = useState<InterestCalculateResponse>();
  const [futureBalance, setFutureBalance] = useState<string>('');
  const [futureBalanceDate, setFutureBalanceDate] = useState<string>('');
  const [futureBalanceTitle, setFutureBalanceTitle] = useState<string>('Future Balance');
  const isSuperAdmin = rbac.isSuperadmin();

  useEffect(() => {
    isModalVisible && group === 'interest' && (async () => {
      const nextCalculation = await creditHttp.interestCalculation(creditId, {
        todayDate,
        creditAmount: query.has('creditAmount') ? +(query.get('creditAmount') || 0) : undefined,
        interestRate: query.has('interestRate') ? +(query.get('interestRate') || 0) : undefined,
        moratoryRate: query.has('moratoryRate') ? +(query.get('moratoryRate') || 0) : undefined,
        signatureDate: query.get('signatureDate') ?? undefined,
        paymentDate: query.get('paymentDate') ?? undefined,
      });
      setCalc(nextCalculation);
    })();
  }, [creditId, group, isModalVisible]);

  useEffect(() => {
    (async () => {
      const payments = calc && Array.isArray(calc.payments) ? calc.payments : undefined;
      const nextCalculation = await creditHttp.interestCalculation(creditId, {
        payments, todayDate,
      });
      setCalc(nextCalculation);
    })();
  }, [search]);

  const rows = calc?.rows || [];

  const { submit: onAddRow, loading: onAddLoading } = useRequest(async () => {
    const payments = calc && Array.isArray(calc.payments) ? calc.payments : undefined;
    Array.isArray(payments) && payments.push({
      paymentAmount: 0,
      paymentDate: calc?.nextDate || dayjs(new Date()).tz('Europe/London').format('YYYY-MM-DD'),
    });
    const nextCalculation = await creditHttp.interestCalculation(creditId, {
      payments, todayDate,
    });
    setCalc(nextCalculation);
  });

  const { submit: onSave, loading: onSaveLoading } = useRequest(async () => {
    const payments = calc?.payments || [];
    const isSave = true;
    const nextCalculation = await creditHttp.interestCalculation(creditId, {
      payments, todayDate, isSave,
    });
    setCalc(nextCalculation);
    notification.success({ message: 'Interests saved!', placement: 'bottom' });

    if (refreshCredit) {
      await refreshCredit();
    }
  });

  const { isExporting, downloadInterestReport } = useInterestReport();
  const downloadInterestReportPDF = () => downloadInterestReport(credit, 'export-credit-interest');

  const onDelete = async (title: string) => {
    const payments = (calc?.payments || []).filter(p => p.paymentTitle !== title);
    const nextCalculation = await creditHttp.interestCalculation(creditId, {
      payments, todayDate,
    });
    setCalc(nextCalculation);
  };

  const onChange = debounce((title: string, field: string, value: string | number) => {
    if (!Array.isArray(calc?.payments)) return;
    const payments = (calc?.payments || []).map(payment => {
      if (payment.paymentTitle === title) {
        payment[field] = value;
      }
      return payment;
    });
    creditHttp.interestCalculation(creditId, {
      payments, todayDate,
    }).then(nextCalc => setCalc(nextCalc));
  });

  const onSelectFutureDate = (date: string) => {
    setFutureBalanceDate(date);
  };

  const calculateOrdinaryTermFutureBalance = ({
    finalBalance,
    capitalBalance,
    interestRateValue,
    fromMoment,
    toMoment,
  }: CalculateFutureBalanceParams) => {
    const days = toMoment.diff(fromMoment, 'days');
    const ordinaryInterests = calculateInterestAmount({
      capitalBalance,
      rate: interestRateValue,
      days,
      dateMoment: toMoment,
    });

    return finalBalance + ordinaryInterests;
  };

  const calculateMixedTermFutureBalance = ({
    finalBalance,
    capitalBalance,
    interestRateValue,
    moratoryRateValue,
    ptMoment,
    paymentMoment,
    futureMoment,
  }: CalculateMixedFutureBalanceParams) => {
    const ordinaryDays = paymentMoment.diff(ptMoment, 'days');
    const moratoryDays = futureMoment.diff(paymentMoment, 'days');
    const ordinaryInterests = calculateInterestAmount({
      capitalBalance,
      rate: interestRateValue,
      days: ordinaryDays,
      dateMoment: paymentMoment,
    });
    const moratoryInterests = calculateInterestAmount({
      capitalBalance,
      rate: moratoryRateValue,
      days: moratoryDays,
      dateMoment: futureMoment,
    });

    return finalBalance + ordinaryInterests + moratoryInterests;
  };

  const calculateMoratoryTermFutureBalance = ({
    finalBalance,
    capitalBalance,
    moratoryRateValue,
    fromMoment,
    futureMoment,
  }: CalculateMoratoryFutureBalanceParams) => {
    const moratoryDays = futureMoment.diff(fromMoment, 'days');
    const moratoryInterests = calculateInterestAmount({
      capitalBalance,
      rate: moratoryRateValue,
      days: moratoryDays,
      dateMoment: futureMoment,
    });

    return finalBalance + moratoryInterests;
  };

  const applyFutureBalanceResult = (title: string, value: number) => {
    setFutureBalanceTitle(title);
    setFutureBalance(money(value) || '0');
  };

  const calculateFutureBalance = () => {
    const postPayment: IInterestRow | null = (
      (calc?.rows || []).find(i => i.paymentTitle === 'P-T')
      || (calc?.rows || []).find(i => i.paymentTitle === 'PD-T')
      || null
    );
    if (postPayment && futureBalanceDate) {
      const { postpaymentFinalBalance, postpaymentCapitalBalance, interestRate } = postPayment;

      if (postpaymentFinalBalance !== null
        && postpaymentCapitalBalance !== null
        && interestRate !== null
      ) {
        const ptDate = postPayment.paymentDate;
        if (ptDate && calc?.paymentDate) {
          const ptMoment = moment(ptDate);
          const paymentMoment = moment(calc.paymentDate);
          const futureMoment = moment(futureBalanceDate, 'DD-MM-YYYY');
          const moratoryRate = calc?.moratoryRate ?? 0;

          if (!futureMoment.isValid() || !paymentMoment.isValid() || !ptMoment.isValid()) return;

          if (!calc?.isDelay && !futureMoment.isAfter(paymentMoment)) {
            const value = calculateOrdinaryTermFutureBalance({
              finalBalance: postpaymentFinalBalance,
              capitalBalance: postpaymentCapitalBalance,
              interestRateValue: interestRate,
              fromMoment: ptMoment,
              toMoment: futureMoment,
            });
            applyFutureBalanceResult('Ordinary Term', value);
            return;
          }

          if (!calc?.isDelay && futureMoment.isAfter(paymentMoment)) {
            const value = calculateMixedTermFutureBalance({
              finalBalance: postpaymentFinalBalance,
              capitalBalance: postpaymentCapitalBalance,
              interestRateValue: interestRate,
              moratoryRateValue: moratoryRate,
              ptMoment,
              paymentMoment,
              futureMoment,
            });
            applyFutureBalanceResult('Ordinary and Moratory mixed Term', value);
            return;
          }

          if (calc?.isDelay && futureMoment.isAfter(paymentMoment)) {
            const value = calculateMoratoryTermFutureBalance({
              finalBalance: postpaymentFinalBalance,
              capitalBalance: postpaymentCapitalBalance,
              moratoryRateValue: moratoryRate,
              fromMoment: ptMoment,
              futureMoment,
            });
            applyFutureBalanceResult('Moratory Term', value);
          }
        }
      }
    }
  };

  if (calc && !calc.riskExists) {
    return <CalcHeader calc={calc} error="Risk Manager not found" />;
  }
  if (calc && !calc.signatureDate) {
    return <CalcHeader calc={calc} error="Missing Disbursement date" />;
  }
  if (calc && !calc.paymentDate) {
    return <CalcHeader calc={calc} error="Missing Payment date" />;
  }
  if (calc && calc.interestRate === 0 && calc.moratoryRate === 0) {
    return <CalcHeader calc={calc} error="Missing Interest/Moratory Rate" />;
  }

  const showAddButton = isSuperAdmin && !calc?.isPaid;

  const {
    user, signedContractAt, paymentAt, interestRateLoan, moratoryInterestRateLoan,
  } = credit;
  const riskReplies = credit?.riskReplies || [];
  const creditTermReply = riskReplies.find(r => r.tag === 'creditTerm');
  const creditTerms = (creditTermReply?.value || '')
    .replace('Months', 'Meses')
    .replace('Month', 'Mes');

  const loc = Array.isArray(user.location) ? user.location[0] : null;
  const address = loc ? [
    [loc.street, loc.house, loc.apartment].filter(Boolean).join(' '), // Calle, Número Ext, Número Int
    loc.neighbourhood, // Colonia
    loc.city, // Municipio
    loc.state, // Estado
    loc.postalCode, // ZIP code
  ].filter(Boolean).join(', ') : null;

  return (
    <>
      <CalcHeader calc={calc} />

      <div id="export-credit-interest">
        {isExporting && (
          <Row gutter={[20, 20]}>
            <Col xs={24} sm={8} className={cls.interestBox}>
              <VerqorIcon />
              <InterestRow title="Acreditado:" text={credit.accreditedName} />
              <InterestRow title="Domicilio:" text={address} />
            </Col>
            <Col xs={24} sm={8} className={cls.interestBox}>
              <div className={cls.interestHeader}>Tabla de Pagos - Crédito Verqor</div>
              <InterestRow title="Crédito:" text={credit.code} />
              <InterestRow title="RFC:" text={credit.user.rfcActual} />
              <InterestRow title="Inicio de crédito:"
                text={signedContractAt && dateFormatter.toDateSlash(signedContractAt)} />
              <InterestRow title="Fecha final de pago:"
                text={paymentAt && dateFormatter.toDateSlash(paymentAt)} />
              <InterestRow title="Corte al:"
                text={dateFormatter.toDateSlash(new Date())} />
            </Col>
            <Col xs={24} sm={8} className={cls.interestBox}>
              <div className={cls.interestHeader}>Resumen</div>
              <InterestRow title="Crédito:" text={moneyFormatter.format(credit.bankApproval)} />
              <InterestRow title="TDI:" text={`${interestRateLoan}% + IVA`} />
              <InterestRow title="TDIM:" text={`${moratoryInterestRateLoan}% + IVA`} />
              <InterestRow title="Plazo:" text={creditTerms} />
            </Col>
          </Row>
        )}

        <TableContainer id="export-credit-interest" className={cls.tableContainer}>
          <Table sx={{ minWidth: 750 }} className={cls.table}>
            <TableHead>
              <TableRow className={cls.rowGroups}>
                <TableCell className={cls.plusCell} id="plusCell">
                  {showAddButton && !isPreview && (
                    <IconButton onClick={onAddRow}
                      disabled={onAddLoading}
                      id="export-credit-add-btn">
                      <PlusSquareFilled className={cls.plusIcon} />
                    </IconButton>
                  )}
                  {!isPreview && (
                    <IconButton onClick={downloadInterestReportPDF} id="export-credit-dl-btn">
                      <DownloadOutlined className={cls.downloadIcon} />
                    </IconButton>
                  )}
                </TableCell>
                <TableCell className={cls.topTh} colSpan={3}>
                  {isExporting ? 'Información de Pagos' : 'Payments Info'}
                </TableCell>
                <TableCell colSpan={4}>{' '}</TableCell>
                <TableCell className={cls.topTh} colSpan={2}>
                  {isExporting ? 'Pre-Pago' : 'Pre-Payment'}
                </TableCell>
                <TableCell className={cls.topTh} colSpan={3}>
                  {isExporting ? 'Post-Pago' : 'Post-Payment'}
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell className={cls.deleteCell}>{' '}</TableCell>
                <TableCell className={clsx(cls.grayBg, cls.numCell)}>No.</TableCell>
                <TableCell className={clsx(cls.grayBg, cls.inputCell)}>
                  {isExporting ? 'Monto de Pago' : 'Amount'}
                </TableCell>
                <TableCell className={clsx(cls.grayBg, cls.inputCell)}>
                  {isExporting ? 'Fecha' : 'Date'}
                </TableCell>
                <TableCell>
                  {isExporting ? 'Saldo Inicial' : 'Prior Capital Balance'}
                </TableCell>
                <TableCell>
                  {isExporting ? 'Intereses Anteriores' : 'Prior Interests'}
                </TableCell>
                <TableCell>
                  {isExporting ? 'Saldo Total Anterior' : 'Total Prior Balance'}
                </TableCell>
                <TableCell>
                  {isExporting ? 'Intereses del periodo' : 'Period Interests'}
                </TableCell>
                <TableCell className={cls.sandBg}>
                  {isExporting ? 'Saldo de Intereses' : 'Interests Balance'}
                </TableCell>
                <TableCell className={cls.sandBg}>
                  {isExporting ? 'Consulta de Saldo' : 'Balance Inquiry'}
                </TableCell>
                <TableCell className={cls.greenBg}>
                  {isExporting ? 'Intereses Pendientes' : 'Interest Balance'}
                </TableCell>
                <TableCell className={cls.greenBg}>
                  {isExporting ? 'Saldo de Capital' : 'Capital Balance'}
                </TableCell>
                <TableCell className={cls.greenBg}>
                  {isExporting ? 'Saldo Final' : 'Final Balance'}
                </TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {rows.map((row, idx) => {
                const {
                  paymentTitle, paymentAmount, paymentDate, isPaid, withdrawalId,
                  priorCapitalBalance, priorInterests, priorTotalBalance, periodInterests,
                  prepeymentInterestsBalance, prepeymentBalanceInquiry,
                  postpaymentInterestsBalance, postpaymentCapitalBalance, postpaymentFinalBalance,
                } = row;
                let removable = (paymentTitle.includes('P-') || paymentTitle.includes('PD-'))
                  && !['P-0', 'P-T', 'P-F', 'PD-T'].includes(paymentTitle);

                const locked = !!withdrawalId;
                if (locked) {
                  removable = false;
                }

                return (
                  <TableRow key={idx} className={clsx(
                    paymentTitle === 'P-T' && cls.rowToday,
                    (paymentTitle === 'P-F' || isPaid) && cls.rowFinal,
                    paymentTitle.includes('PD-') && paymentTitle !== 'PD-T' && cls.rowDelay,
                    paymentTitle === 'PD-T' && cls.rowDelayToToday,
                    cls.numCell,
                  )}>
                    <TableCell className={clsx(cls.deleteCell, 'delBtn')}>
                      {removable && !isExporting ? (
                        <ConfirmDelete cb={() => onDelete(paymentTitle)}>
                          <IconButton><DeleteOutlined className={cls.deleteIcon} /></IconButton>
                        </ConfirmDelete>
                      ) : null}
                    </TableCell>

                    <TableCell>{row.paymentPaidTitle || row.paymentTitle}</TableCell>

                    <TableCell className={cls.inputCell}>
                      {typeof paymentAmount === 'number' ? (
                        <InputNumber
                          disabled={locked}
                          prefix={
                            <DollarCircleOutlined className={clsx(cls.amountIcon, 'amountIcon')} />}
                          bordered={+paymentAmount < 0}
                          value={paymentAmount}
                          onChange={cost => onChange(paymentTitle, 'paymentAmount', +`${cost}`)}
                          controls={false}
                          onKeyDown={locked ? e => e.preventDefault() : undefined}
                          className={clsx(cls.amountInput, 'amountInput')}
                          min={0} max={999999999}
                          status={+paymentAmount >= 0 ? undefined : 'error'}
                          maxLength={10}
                        />
                      ) : null}
                    </TableCell>

                    <TableCell className={cls.inputCell}>
                      {removable ? (
                        <DatePicker
                          className={cls.dateInput}
                          bordered={!paymentDate}
                          status={paymentDate ? undefined : 'error'}
                          allowClear={false}
                          disabled={locked}
                          value={paymentDate ? moment(paymentDate).tz(tz) : null}
                          onKeyDown={event => {
                            if (locked) return event.preventDefault();
                            const input = event.target as HTMLInputElement;
                            input.value = MASKED.resolve(input.value);
                            setTimeout(() => {
                              const momentDate = moment(input.value, DATE_FORMAT, true).tz(tz);
                              if (momentDate.isValid()) {
                                onChange(paymentTitle, 'paymentDate', momentDate.format('YYYY-MM-DD'));
                              }
                            }, 200);
                          }}
                          onChange={selectedValue => {
                            if (locked) return;
                            const momentDate = moment(selectedValue, DATE_FORMAT, true).tz(tz);
                            if (momentDate.isValid()) {
                              onChange(paymentTitle, 'paymentDate', momentDate.format('YYYY-MM-DD'));
                            }
                          }}
                          picker="date"
                          format={DATE_FORMAT}
                          disabledDate={(current) => current && current > moment().tz(tz).endOf('day')}
                        />
                      ) : null}
                      {typeof paymentDate === 'string' && !removable
                        ? dateFormatter.toDateSlash(paymentDate)
                        : null}
                    </TableCell>

                    <TableCell>
                      {typeof priorCapitalBalance === 'number' && money(priorCapitalBalance)}
                    </TableCell>
                    <TableCell>
                      {typeof priorInterests === 'number' && money(priorInterests)}
                    </TableCell>
                    <TableCell>
                      {typeof priorTotalBalance === 'number' && money(priorTotalBalance)}
                    </TableCell>
                    <TableCell>
                      {typeof periodInterests === 'number' && money(periodInterests)}
                    </TableCell>
                    <TableCell>
                      {typeof prepeymentInterestsBalance === 'number' && money(prepeymentInterestsBalance)}
                    </TableCell>
                    <TableCell>
                      {typeof prepeymentBalanceInquiry === 'number' && money(prepeymentBalanceInquiry)}
                    </TableCell>
                    <TableCell>
                      {typeof postpaymentInterestsBalance === 'number' && money(postpaymentInterestsBalance)}
                    </TableCell>
                    <TableCell>
                      {typeof postpaymentCapitalBalance === 'number' && money(postpaymentCapitalBalance)}
                    </TableCell>
                    <TableCell>
                      {typeof postpaymentFinalBalance === 'number' && money(postpaymentFinalBalance)}
                    </TableCell>
                  </TableRow>
                );
              })}
              <TableRow className={cls.rowTotal}>
                <TableCell colSpan={2}>{' '}</TableCell>
                <TableCell colSpan={2}>
                  {money(calc?.paymentAmount) || 0}{calc?.isUsd && ' USD'} – {isExporting ? 'Monto de Pago' : 'Payment Amount'}
                </TableCell>
                <TableCell colSpan={3}>
                  {calc?.savingsAmount && calc?.savingsAmount > 0 ? (
                    <>{money(calc?.savingsAmount) || 0}{calc?.isUsd && ' USD'} – {isExporting ? 'Ahorros por Pago anticipado' : 'Early payment savings'}</>
                  ) : null}
                </TableCell>
                <TableCell colSpan={7}>
                  {money(calc?.interestsAmount ?? 0) || 0}{calc?.isUsd && ' USD'} – {isExporting ? 'Saldo de Intereses' : 'Interests Amount'}
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      </div>

      <div className={cls.footer}>
        <div className={cls.calculatorSection}>
          <div className={cls.calculatorTitle}>{futureBalanceTitle}</div>
          <div className={cls.calculator}>
            <DatePicker
              className={cls.calculatorDate}
              allowClear={false}
              picker="date"
              format={DATE_FORMAT}
              disabledDate={(current) => current && current < moment().tz(tz).endOf('day')}
              onChange={selectedValue => {
                const momentDate = moment(selectedValue, DATE_FORMAT, true).tz(tz);
                if (momentDate.isValid()) {
                  onSelectFutureDate(momentDate.format('DD-MM-YYYY'));
                }
              }}
            />
            <button type="button" className={cls.calculatorBtn} onClick={calculateFutureBalance}>
              Calculate
            </button>
            <div className={cls.calculatorValue}>{futureBalance}</div>
          </div>
        </div>
        <div className={cls.actions}>
          {isPreview ? (
            <Button
              variant="contained"
              disabled={isExporting}
              onClick={downloadInterestReportPDF}
              className={cls.btn}
            >
              <span>Interest Table</span>
            </Button>
          ) : (
            <Button
              variant="contained"
              disabled={onSaveLoading}
              onClick={onSave}
              className={cls.btn}
            >
              <SaveIcon /><span>Save</span>
            </Button>
          )}
        </div>
      </div>
    </>
  );
};

const InterestRow = ({ title, text }: {title: string, text: any}) => {
  return (
    <div className={cls.interestRow}>
      <div className={cls.interestTitle}>{title}</div>
      <div className={cls.interestText}>{text}</div>
    </div>
  );
};

const money = (amount: number | undefined) => amount && amount > 0
  ? moneyFormatter.format(amount)
  : null;

const DATE_FORMAT = 'DD/MM/YYYY';
const MASKED = IMask.createMask({
  blocks: {
    DD: { from: 1, mask: IMask.MaskedRange, to: 31 },
    MM: { from: 1, mask: IMask.MaskedRange, to: 12 },
    YYYY: { from: 1900, mask: IMask.MaskedRange, to: new Date().getFullYear() },
  },
  format: (date: Date) => moment(date).tz(tz).format(DATE_FORMAT),
  mask: Date,
  parse: (date: string) => moment(date, DATE_FORMAT).tz(tz),
  pattern: DATE_FORMAT,
});

const SaveIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" style={{ display: 'inline-block' }}
    width="15"
    height="15"
    viewBox="0 0 15 15"
    fill="none">
    <g clipPath="url(#clip0_3843_4772)">
      <path d="M14.8964 1.75077L13.2321 0.10257C13.1643 0.0389059 13.075 0 12.9786 0H2.25C2.21429 0 2.18571 0 2.15 0C2.15 0 2.14643 0 2.14286 0H0.357143C0.160714 0 0 0.159161 0 0.35369V14.6463C0 14.8408 0.160714 15 0.357143 15H14.6429C14.8393 15 15 14.8408 15 14.6463V2.00189C15 1.90639 14.9607 1.81797 14.8964 1.75077ZM7.5 11.8486C6.11786 11.8486 5 10.7416 5 9.37279C5 8.00401 6.11786 6.89696 7.5 6.89696C8.88214 6.89696 10 8.00401 10 9.37279C10 10.7416 8.88214 11.8486 7.5 11.8486ZM11.4286 3.90474C11.4286 4.49187 10.95 4.96581 10.3571 4.96581H4.64286C4.05 4.96581 3.57143 4.49187 3.57143 3.90474V1.40769C3.57143 1.22377 3.72143 1.07522 3.90714 1.07522L7.91071 1.06814C8.07857 1.06814 8.21429 1.20255 8.21429 1.36878V3.47324C8.21429 3.70667 8.40357 3.89413 8.63929 3.89413H9.575C9.81071 3.89413 10 3.70667 10 3.47324V1.36171C10 1.19547 10.1357 1.06107 10.3036 1.06107H11.0929C11.2786 1.06107 11.4286 1.20962 11.4286 1.39354V3.90474Z"
        fill="#E1E5ED" />
    </g>
    <defs>
      <clipPath id="clip0_3843_4772">
        <rect width="15" height="15" fill="white" />
      </clipPath>
    </defs>
  </svg>
);

const VerqorIcon = () => (
  <svg width="205" height="84" viewBox="0 0 205 84" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M3.9243 83L0.3937 74.1862H1.6891L4.5974 81.6411H4.1275L7.0612 74.1862H8.3439L4.7879 83H3.9243ZM11.0551 83V74.1862H12.2489V83H11.0551ZM11.8679 83V81.9078H16.9225V83H11.8679ZM11.8679 79.0122V77.9708H16.5034V79.0122H11.8679ZM11.8679 75.2784V74.1862H16.859V75.2784H11.8679ZM20.6162 79.1646V78.1613H23.0038C23.5202 78.1613 23.9182 78.0301 24.1976 77.7676C24.477 77.5051 24.6167 77.1495 24.6167 76.7008C24.6167 76.2775 24.477 75.9261 24.1976 75.6467C23.9266 75.3673 23.5329 75.2276 23.0165 75.2276H20.6162V74.1862H23.0546C23.6134 74.1862 24.1002 74.2963 24.5151 74.5164C24.9299 74.7281 25.2474 75.0202 25.4676 75.3927C25.6962 75.7652 25.8105 76.1928 25.8105 76.6754C25.8105 77.1749 25.6962 77.611 25.4676 77.9835C25.2474 78.356 24.9299 78.6481 24.5151 78.8598C24.1002 79.063 23.6134 79.1646 23.0546 79.1646H20.6162ZM19.8161 83V74.1862H21.0099V83H19.8161ZM24.8961 83L21.6957 79.0503L22.826 78.6566L26.4074 83H24.8961ZM32.8014 83.127C32.1749 83.127 31.5907 83.0127 31.0488 82.7841C30.507 82.547 30.0328 82.2211 29.6264 81.8062C29.22 81.3829 28.9025 80.896 28.6739 80.3457C28.4453 79.7954 28.331 79.2069 28.331 78.5804C28.331 77.9454 28.4453 77.357 28.6739 76.8151C28.9025 76.2648 29.2158 75.7822 29.6137 75.3673C30.0201 74.9524 30.49 74.6307 31.0234 74.4021C31.5653 74.1735 32.1495 74.0592 32.776 74.0592C33.4026 74.0592 33.9825 74.1735 34.5159 74.4021C35.0578 74.6307 35.5319 74.9524 35.9383 75.3673C36.3447 75.7822 36.658 76.2648 36.8781 76.8151C37.1067 77.3654 37.221 77.9581 37.221 78.5931C37.221 79.2196 37.1067 79.8081 36.8781 80.3584C36.658 80.9087 36.3447 81.3913 35.9383 81.8062C35.5404 82.2211 35.0705 82.547 34.5286 82.7841C33.9952 83.0127 33.4195 83.127 32.8014 83.127ZM37.0178 83.6223L32.8903 79.4948L33.665 78.7201L37.7798 82.8476L37.0178 83.6223ZM32.776 81.984C33.4026 81.984 33.9571 81.8401 34.4397 81.5522C34.9223 81.2559 35.2991 80.8537 35.57 80.3457C35.8494 79.8292 35.9891 79.2408 35.9891 78.5804C35.9891 78.0893 35.9087 77.6406 35.7478 77.2342C35.5954 76.8193 35.3753 76.4595 35.0874 76.1547C34.7996 75.8499 34.4609 75.6171 34.0714 75.4562C33.682 75.2869 33.2502 75.2022 32.776 75.2022C32.1495 75.2022 31.5949 75.3461 31.1123 75.634C30.6297 75.9219 30.2487 76.3198 29.9693 76.8278C29.6984 77.3358 29.5629 77.92 29.5629 78.5804C29.5629 79.0715 29.6434 79.5287 29.8042 79.952C29.9651 80.3669 30.1852 80.7267 30.4646 81.0315C30.7525 81.3278 31.0912 81.5607 31.4806 81.73C31.8786 81.8993 32.3104 81.984 32.776 81.984ZM44.4395 83.127C43.8129 83.127 43.2287 83.0127 42.6869 82.7841C42.145 82.547 41.6709 82.2211 41.2645 81.8062C40.8581 81.3829 40.5406 80.896 40.312 80.3457C40.0918 79.7954 39.9818 79.2069 39.9818 78.5804C39.9818 77.9454 40.0918 77.357 40.312 76.8151C40.5406 76.2648 40.8538 75.7822 41.2518 75.3673C41.6582 74.9524 42.1281 74.6307 42.6615 74.4021C43.2033 74.1735 43.7875 74.0592 44.4141 74.0592C45.0406 74.0592 45.6206 74.1735 46.154 74.4021C46.6958 74.6307 47.17 74.9524 47.5764 75.3673C47.9828 75.7822 48.3003 76.2648 48.5289 76.8151C48.7575 77.3654 48.8718 77.9581 48.8718 78.5931C48.8718 79.2196 48.7575 79.8081 48.5289 80.3584C48.3003 80.9087 47.9828 81.3913 47.5764 81.8062C47.1784 82.2211 46.7085 82.547 46.1667 82.7841C45.6333 83.0127 45.0575 83.127 44.4395 83.127ZM44.4141 81.984C45.0491 81.984 45.6036 81.8401 46.0778 81.5522C46.5604 81.2559 46.9414 80.8537 47.2208 80.3457C47.5002 79.8292 47.6399 79.2408 47.6399 78.5804C47.6399 78.0893 47.5594 77.6406 47.3986 77.2342C47.2377 76.8193 47.0133 76.4595 46.7255 76.1547C46.4376 75.8499 46.0989 75.6171 45.7095 75.4562C45.32 75.2869 44.8882 75.2022 44.4141 75.2022C43.7875 75.2022 43.233 75.3461 42.7504 75.634C42.2762 75.9219 41.8995 76.3198 41.6201 76.8278C41.3491 77.3358 41.2137 77.92 41.2137 78.5804C41.2137 79.0715 41.2899 79.5287 41.4423 79.952C41.6031 80.3669 41.8233 80.7267 42.1027 81.0315C42.3905 81.3278 42.7292 81.5607 43.1187 81.73C43.5166 81.8993 43.9484 81.984 44.4141 81.984ZM52.5795 79.1646V78.1613H54.9671C55.4836 78.1613 55.8815 78.0301 56.1609 77.7676C56.4403 77.5051 56.58 77.1495 56.58 76.7008C56.58 76.2775 56.4403 75.9261 56.1609 75.6467C55.89 75.3673 55.4963 75.2276 54.9798 75.2276H52.5795V74.1862H55.0179C55.5767 74.1862 56.0635 74.2963 56.4784 74.5164C56.8933 74.7281 57.2108 75.0202 57.4309 75.3927C57.6595 75.7652 57.7738 76.1928 57.7738 76.6754C57.7738 77.1749 57.6595 77.611 57.4309 77.9835C57.2108 78.356 56.8933 78.6481 56.4784 78.8598C56.0635 79.063 55.5767 79.1646 55.0179 79.1646H52.5795ZM51.7794 83V74.1862H52.9732V83H51.7794ZM56.8594 83L53.659 79.0503L54.7893 78.6566L58.3707 83H56.8594ZM68.9611 83.127C68.3261 83.127 67.7377 83.0127 67.1958 82.7841C66.6539 82.547 66.1798 82.2211 65.7734 81.8062C65.3755 81.3913 65.0664 80.9087 64.8463 80.3584C64.6262 79.8081 64.5161 79.2196 64.5161 78.5931C64.5161 77.9581 64.6262 77.3654 64.8463 76.8151C65.0664 76.2648 65.3755 75.7864 65.7734 75.38C66.1798 74.9651 66.6497 74.6434 67.1831 74.4148C67.725 74.1777 68.3134 74.0592 68.9484 74.0592C69.6427 74.0592 70.2523 74.1777 70.7772 74.4148C71.3021 74.6519 71.7678 74.9736 72.1742 75.38L71.3487 76.2055C71.0693 75.8922 70.7264 75.6467 70.32 75.4689C69.9221 75.2911 69.4649 75.2022 68.9484 75.2022C68.4827 75.2022 68.0552 75.2869 67.6657 75.4562C67.2762 75.6171 66.9376 75.8499 66.6497 76.1547C66.3618 76.4595 66.1375 76.8193 65.9766 77.2342C65.8242 77.6491 65.748 78.102 65.748 78.5931C65.748 79.0842 65.8242 79.5371 65.9766 79.952C66.1375 80.3669 66.3618 80.7267 66.6497 81.0315C66.9376 81.3363 67.2762 81.5734 67.6657 81.7427C68.0552 81.9036 68.4827 81.984 68.9484 81.984C69.4987 81.984 69.9771 81.8951 70.3835 81.7173C70.7899 81.531 71.1328 81.2813 71.4122 80.968L72.2377 81.7808C71.8313 82.2041 71.3529 82.5343 70.8026 82.7714C70.2607 83.0085 69.6469 83.127 68.9611 83.127ZM74.1033 83L77.7101 74.1862H78.5737L82.1678 83H80.8724L77.9133 75.5451H78.3578L75.3733 83H74.1033ZM75.8178 81.0061V79.9647H80.4533V81.0061H75.8178ZM85.5648 79.571V78.5296H87.9651C88.2953 78.5296 88.5874 78.4619 88.8414 78.3264C89.1038 78.1909 89.307 78.0004 89.451 77.7549C89.6034 77.5094 89.6796 77.2173 89.6796 76.8786C89.6796 76.5399 89.6034 76.2478 89.451 76.0023C89.307 75.7568 89.1038 75.5663 88.8414 75.4308C88.5874 75.2953 88.2953 75.2276 87.9651 75.2276H85.5648V74.1862H88.0413C88.5831 74.1862 89.0657 74.2963 89.4891 74.5164C89.9209 74.7365 90.2595 75.0498 90.5051 75.4562C90.7591 75.8541 90.8861 76.3283 90.8861 76.8786C90.8861 77.4205 90.7591 77.8946 90.5051 78.301C90.2595 78.6989 89.9209 79.0122 89.4891 79.2408C89.0657 79.4609 88.5831 79.571 88.0413 79.571H85.5648ZM84.7647 83V74.1862H85.9585V83H84.7647ZM93.6373 83V74.1862H94.8311V83H93.6373ZM100.518 83V74.4402H101.712V83H100.518ZM97.5211 75.2784V74.1862H104.697V75.2784H97.5211ZM105.451 83L109.058 74.1862H109.922L113.516 83H112.221L109.261 75.5451H109.706L106.721 83H105.451ZM107.166 81.0061V79.9647H111.801V81.0061H107.166ZM116.113 83V74.1862H117.307V83H116.113ZM116.926 83V81.9078H121.65V83H116.926ZM130.654 83.127C129.959 83.127 129.367 83 128.876 82.746C128.385 82.492 127.949 82.1237 127.568 81.6411L128.38 80.8283C128.66 81.2178 128.982 81.5141 129.346 81.7173C129.71 81.912 130.158 82.0094 130.692 82.0094C131.217 82.0094 131.636 81.8951 131.949 81.6665C132.271 81.4379 132.432 81.1246 132.432 80.7267C132.432 80.3965 132.356 80.1298 132.203 79.9266C132.051 79.7234 131.843 79.5583 131.581 79.4313C131.327 79.2958 131.047 79.1773 130.743 79.0757C130.438 78.9656 130.133 78.8513 129.828 78.7328C129.523 78.6058 129.244 78.4534 128.99 78.2756C128.736 78.0893 128.529 77.848 128.368 77.5517C128.215 77.2554 128.139 76.8828 128.139 76.4341C128.139 75.9346 128.258 75.5112 128.495 75.1641C128.74 74.8085 129.07 74.5376 129.485 74.3513C129.909 74.1566 130.383 74.0592 130.908 74.0592C131.483 74.0592 132 74.1735 132.457 74.4021C132.914 74.6222 133.287 74.9143 133.575 75.2784L132.762 76.0912C132.499 75.7864 132.216 75.5578 131.911 75.4054C131.615 75.253 131.272 75.1768 130.882 75.1768C130.408 75.1768 130.031 75.2826 129.752 75.4943C129.473 75.6975 129.333 75.9854 129.333 76.3579C129.333 76.6542 129.409 76.8955 129.562 77.0818C129.722 77.2596 129.93 77.412 130.184 77.539C130.438 77.666 130.717 77.7845 131.022 77.8946C131.335 77.9962 131.644 78.1105 131.949 78.2375C132.254 78.3645 132.533 78.5254 132.787 78.7201C133.041 78.9148 133.245 79.1688 133.397 79.4821C133.558 79.7869 133.638 80.1721 133.638 80.6378C133.638 81.4167 133.367 82.0263 132.825 82.4666C132.292 82.9069 131.568 83.127 130.654 83.127ZM135.584 83L139.191 74.1862H140.055L143.649 83H142.353L139.394 75.5451H139.839L136.854 83H135.584ZM137.299 81.0061V79.9647H141.934V81.0061H137.299ZM147.046 79.571V78.5296H149.446C149.776 78.5296 150.068 78.4619 150.322 78.3264C150.585 78.1909 150.788 78.0004 150.932 77.7549C151.084 77.5094 151.16 77.2173 151.16 76.8786C151.16 76.5399 151.084 76.2478 150.932 76.0023C150.788 75.7568 150.585 75.5663 150.322 75.4308C150.068 75.2953 149.776 75.2276 149.446 75.2276H147.046V74.1862H149.522C150.064 74.1862 150.547 74.2963 150.97 74.5164C151.402 74.7365 151.74 75.0498 151.986 75.4562C152.24 75.8541 152.367 76.3283 152.367 76.8786C152.367 77.4205 152.24 77.8946 151.986 78.301C151.74 78.6989 151.402 79.0122 150.97 79.2408C150.547 79.4609 150.064 79.571 149.522 79.571H147.046ZM146.246 83V74.1862H147.439V83H146.246ZM155.118 83V74.1862H156.312V83H155.118ZM164.362 83V81.9078H166.813C167.465 81.9078 168.036 81.7681 168.527 81.4887C169.018 81.2008 169.399 80.8071 169.67 80.3076C169.95 79.8081 170.089 79.2323 170.089 78.5804C170.089 77.9369 169.95 77.3654 169.67 76.8659C169.391 76.3664 169.005 75.9769 168.514 75.6975C168.023 75.4181 167.456 75.2784 166.813 75.2784H164.374V74.1862H166.838C167.481 74.1862 168.074 74.2963 168.616 74.5164C169.166 74.7365 169.64 75.0456 170.038 75.4435C170.445 75.8414 170.758 76.3071 170.978 76.8405C171.207 77.3739 171.321 77.9581 171.321 78.5931C171.321 79.2196 171.207 79.8038 170.978 80.3457C170.758 80.8791 170.449 81.3448 170.051 81.7427C169.653 82.1406 169.179 82.4497 168.629 82.6698C168.087 82.8899 167.498 83 166.863 83H164.362ZM163.574 83V74.1862H164.768V83H163.574ZM174.245 83V74.1862H175.439V83H174.245ZM175.058 83V81.9078H180.112V83H175.058ZM175.058 79.0122V77.9708H179.693V79.0122H175.058ZM175.058 75.2784V74.1862H180.049V75.2784H175.058ZM190.918 83.127C190.283 83.127 189.695 83.0127 189.153 82.7841C188.611 82.547 188.137 82.2211 187.731 81.8062C187.333 81.3913 187.024 80.9087 186.804 80.3584C186.583 79.8081 186.473 79.2196 186.473 78.5931C186.473 77.9581 186.583 77.3654 186.804 76.8151C187.024 76.2648 187.333 75.7864 187.731 75.38C188.137 74.9651 188.607 74.6434 189.14 74.4148C189.682 74.1777 190.271 74.0592 190.906 74.0592C191.6 74.0592 192.209 74.1777 192.734 74.4148C193.259 74.6519 193.725 74.9736 194.131 75.38L193.306 76.2055C193.027 75.8922 192.684 75.6467 192.277 75.4689C191.879 75.2911 191.422 75.2022 190.906 75.2022C190.44 75.2022 190.012 75.2869 189.623 75.4562C189.233 75.6171 188.895 75.8499 188.607 76.1547C188.319 76.4595 188.095 76.8193 187.934 77.2342C187.781 77.6491 187.705 78.102 187.705 78.5931C187.705 79.0842 187.781 79.5371 187.934 79.952C188.095 80.3669 188.319 80.7267 188.607 81.0315C188.895 81.3363 189.233 81.5734 189.623 81.7427C190.012 81.9036 190.44 81.984 190.906 81.984C191.456 81.984 191.934 81.8951 192.341 81.7173C192.747 81.531 193.09 81.2813 193.369 80.968L194.195 81.7808C193.789 82.2041 193.31 82.5343 192.76 82.7714C192.218 83.0085 191.604 83.127 190.918 83.127ZM199.814 83L196.284 74.1862H197.579L200.487 81.6411H200.018L202.951 74.1862H204.234L200.678 83H199.814Z"
      fill="black" />
    <path d="M23.3574 61.3979L0 14.0216C1.87812 13.87 3.71609 13.3217 5.613 13.2306C10.0357 13.0181 14.4904 14.5246 16.9404 18.3273C19.1285 21.7235 20.5888 25.7981 22.3517 29.4392C25.1346 35.1871 28.3052 40.7579 31.0931 46.5055C31.2882 46.8715 31.0593 47.2684 30.9006 47.608C29.2137 51.219 26.9177 55.2444 24.9629 58.7604C24.4669 59.6523 23.9834 60.5885 23.3574 61.3979Z"
      fill="#F48326" />
    <path d="M42.4036 33.6949C42.3772 33.5618 42.4209 33.4771 42.4659 33.3614C42.8379 32.4034 44.4315 30.1689 45.0782 29.1822C46.4534 27.0839 47.9153 24.9263 49.6023 23.0651C51.2429 21.2551 53.259 19.6288 54.8791 17.8118C55.9408 16.621 55.6434 16.6671 54.3448 17.279C50.5532 19.0661 46.3469 23.3547 43.7696 26.6332C42.159 28.682 40.8521 30.9379 39.2856 33.0165C38.5467 27.7378 40.3919 22.0667 43.6063 17.8771C47.1389 13.2731 53.9827 9.19386 58.8981 5.98977C62.0251 3.95132 65.2601 2.0682 68.3673 0C68.5482 4.65376 67.345 9.13692 66.0177 13.5422C64.4183 18.8506 62.0231 26.151 57.7703 29.8541C53.5702 33.5114 47.807 34.3851 42.4036 33.6949Z"
      fill="#73BA47" />
    <path d="M41.9679 34.3923C48.2746 35.2318 54.987 34.1226 59.5236 29.3989L47.3387 53.4855C45.0978 57.0267 43.6458 62.6407 38.9154 63.5159C36.2938 64.001 30.2073 64.0228 27.4901 63.7771C26.2281 63.6629 26.703 63.0237 27.0781 62.1136C27.8418 60.2606 29.0508 58.1224 29.9864 56.3075C33.8 48.9106 38.0355 41.7244 41.9679 34.3923Z"
      fill="#003086" />
    <path d="M66.5337 63.6292V34.1213H86.2232V39.0322H72.0077V46.0599H85.2896V50.9285H72.0077V58.7183H86.2232V63.6292H66.5337Z"
      fill="#003086" />
    <path d="M91.3842 63.6292V34.1213H101.144C103.549 34.1213 105.487 34.4741 106.958 35.1797C108.429 35.8571 109.504 36.8449 110.183 38.1432C110.89 39.4132 111.243 40.9232 111.243 42.6731C111.243 45.3826 110.466 47.4852 108.91 48.9811C107.382 50.4487 105.19 51.2672 102.332 51.4366C101.682 51.4648 101.017 51.4789 100.338 51.4789C99.6589 51.4789 99.079 51.4789 98.5981 51.4789H96.8583V63.6292H91.3842ZM107.467 63.6292L100.041 50.8439L105.642 50.1665L113.79 63.6292H107.467ZM96.8583 46.568H100.592C101.526 46.568 102.375 46.4692 103.139 46.2716C103.931 46.0458 104.567 45.6648 105.048 45.1285C105.529 44.5641 105.769 43.7738 105.769 42.7578C105.769 41.7135 105.529 40.9232 105.048 40.387C104.567 39.8507 103.931 39.4979 103.139 39.3286C102.375 39.131 101.526 39.0322 100.592 39.0322H96.8583V46.568Z"
      fill="#003086" />
    <path d="M129.924 64.3066C127.124 64.3066 124.662 63.6715 122.541 62.4015C120.419 61.1032 118.75 59.2969 117.533 56.9825C116.345 54.6682 115.751 51.9728 115.751 48.8964C115.751 45.7918 116.345 43.0964 117.533 40.8103C118.722 38.496 120.376 36.6897 122.498 35.3914C124.648 34.0931 127.124 33.4439 129.924 33.4439C132.753 33.4439 135.228 34.0931 137.35 35.3914C139.5 36.6614 141.169 38.4536 142.357 40.768C143.546 43.0823 144.14 45.7918 144.14 48.8964C144.14 51.9728 143.531 54.6682 142.315 56.9825C141.127 59.2969 139.458 61.1032 137.308 62.4015C135.186 63.6715 132.725 64.3066 129.924 64.3066ZM142.442 66L130.349 54.3154L133.574 50.9285L145.752 62.7825L142.442 66ZM129.882 59.057C131.692 59.057 133.234 58.6477 134.507 57.8292C135.808 56.9825 136.813 55.7971 137.52 54.273C138.227 52.749 138.581 50.9567 138.581 48.8964C138.581 46.8079 138.227 45.0015 137.52 43.4775C136.813 41.9534 135.808 40.7821 134.507 39.9636C133.234 39.1169 131.692 38.6935 129.882 38.6935C128.156 38.6935 126.628 39.1028 125.299 39.9213C123.998 40.7398 122.993 41.911 122.286 43.4351C121.607 44.9592 121.268 46.7796 121.268 48.8964C121.268 50.985 121.607 52.7913 122.286 54.3154C122.993 55.8395 123.998 57.0107 125.299 57.8292C126.628 58.6477 128.156 59.057 129.882 59.057Z"
      fill="#003086" />
    <path d="M163.56 64.3066C160.759 64.3066 158.284 63.6715 156.134 62.4015C154.012 61.1032 152.357 59.2969 151.169 56.9825C149.981 54.6682 149.387 51.9728 149.387 48.8964C149.387 45.7918 149.981 43.0823 151.169 40.768C152.357 38.4536 154.012 36.6614 156.134 35.3914C158.284 34.0931 160.759 33.4439 163.56 33.4439C166.389 33.4439 168.864 34.0931 170.986 35.3914C173.136 36.6614 174.805 38.4536 175.993 40.768C177.181 43.0823 177.775 45.7918 177.775 48.8964C177.775 51.9728 177.167 54.6682 175.951 56.9825C174.763 59.2969 173.094 61.1032 170.944 62.4015C168.822 63.6715 166.361 64.3066 163.56 64.3066ZM163.518 59.057C165.328 59.057 166.87 58.6477 168.143 57.8292C169.444 56.9825 170.448 55.7971 171.156 54.273C171.863 52.749 172.217 50.9567 172.217 48.8964C172.217 46.8079 171.863 45.0015 171.156 43.4775C170.448 41.9534 169.444 40.7821 168.143 39.9636C166.87 39.1169 165.328 38.6935 163.518 38.6935C161.792 38.6935 160.264 39.1028 158.935 39.9213C157.633 40.7398 156.629 41.911 155.922 43.4351C155.243 44.9592 154.903 46.7796 154.903 48.8964C154.903 50.985 155.243 52.7913 155.922 54.3154C156.629 55.8395 157.633 57.0107 158.935 57.8292C160.264 58.6477 161.792 59.057 163.518 59.057Z"
      fill="#003086" />
    <path d="M182.595 63.6292V34.1213H192.355C194.759 34.1213 196.697 34.4741 198.168 35.1797C199.639 35.8571 200.714 36.8449 201.393 38.1432C202.1 39.4132 202.454 40.9232 202.454 42.6731C202.454 45.3826 201.676 47.4852 200.12 48.9811C198.592 50.4487 196.4 51.2672 193.543 51.4366C192.892 51.4648 192.227 51.4789 191.548 51.4789C190.869 51.4789 190.289 51.4789 189.809 51.4789H188.069V63.6292H182.595ZM198.677 63.6292L191.251 50.8439L196.853 50.1665L205 63.6292H198.677ZM188.069 46.568H191.803C192.736 46.568 193.585 46.4692 194.349 46.2716C195.141 46.0458 195.778 45.6648 196.259 45.1285C196.739 44.5641 196.98 43.7738 196.98 42.7578C196.98 41.7135 196.739 40.9232 196.259 40.387C195.778 39.8507 195.141 39.4979 194.349 39.3286C193.585 39.131 192.736 39.0322 191.803 39.0322H188.069V46.568Z"
      fill="#003086" />
  </svg>
);

const debounce = (callback: any, wait = 300) => {
  let timeoutId: any;

  return (...args) => {
    window.clearTimeout(timeoutId);
    timeoutId = window.setTimeout(() => {
      callback(...args);
    }, wait);
  };
};
